import { type JanusCtx, type Row, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, upper, isInventoryRow, inventoryId } from '../context.js';
import { type JanusInput } from '../janus.js';
import { castSkillEntries } from '../charged-cast.js';
import { skillRollArr } from '../skill-roll.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { damagingForcedMove } from '../movement/check-push-and-pull-after-hit.js';
import { damageTypeRoll, withFragmentGateStatuses } from './damage-type-roll.js';
import { effectTargetProceeds } from '../is-target-valid-for-effect.js';
import { applyPreRollEvade } from '../reaction-inventory.js';
import { runIncomingAllyReactions } from '../k4-boundary-reactions.js';
import { critGates, deathTargetAutoCritical, attackCriticalHitThreshold, hinderedAttackStacks, isFlanked, legacyCoverEnvironmentalAcDelta, checkLineOfEffect, combatCenterDistanceFt, rulesWithinReach } from '../templates/template-weapon-attack-roll.js';
import { checkOpposingTeam } from '../check-opposing-team.js';
import { pickStrayCandidate, strayCandidates } from '../stray-shot.js';
import { spellAttackDirectionalCover, attackElevationAdv } from '../cover/directional-cover.js';
import { coverDefenseSnapshot, PARTIAL_DEFENSE_DELTA, HEAVY_DEFENSE_DELTA, type CoverIgnoreFlags, type CoverTier } from '../../actions/cover-defense.js';
import { combatCenter, combatSizeFt } from '../../actions/ability-preconditions.js';
import { selectCastingSkill } from '../summon-caster-cantrips.js';
import { selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { postRollReactionsPreDraw, runPostRollReduce, runPostRollRetaliation } from '../post-roll-reactions.js';
import { isThrowObjectCast, throwObjectSkill } from '../throw-object.js';
import { nextSpellPlan } from '../../sanguine/next-spell.js';
import { planObjectWeaponProjectile } from '../object-weapon-attack.js';

const SPELL_MELEE = '5740CA49-006E-A7A0-B46B-6DDED6CD546C';
const SPELL_RANGED = 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C';
const WEAPON_MELEE = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
const WEAPON_RANGED = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
const rangedAttack = (type: string): boolean => type === SPELL_RANGED || type === WEAPON_RANGED;
const meleeAttack = (type: string): boolean => type === SPELL_MELEE || type === WEAPON_MELEE;
const EVADE = 'BE4CCA95-33F0-323D-BA54-844D2540C0F0';
const UNDERWATER = '19BCE18F-E1DC-D359-48A0-3B789741E058';
const DEAD_EYE = 'D11FB4D0-3A08-A17D-5151-9AEAA1F78660';

interface AttackSpatial { effectiveAc: number; adv: number; underwaterMiss: boolean; coverTier?: CoverTier }
const phpNonEmpty = (value: unknown): boolean => value !== null && value !== undefined && value !== false
  && value !== 0 && value !== '0' && value !== '' && (!Array.isArray(value) || value.length > 0);

/** Resolve every spatial fact before the first d20, including facts for a possible stray retarget. */
function attackSpatial(ctx: JanusCtx, ability: Row, caster: Row, target: Row, rollType: string): AttackSpatial {
  const dc = Math.max(0, requiredInt(ctx.read.value(target, 'ec_total'), 'attack_roll:ec-not-held'));
  let tier: 'none' | 'partial' | 'heavy' | 'blocked' = 'none';
  const attackContext = record(ability.attack_ability_arr);
  const enabled = (row: Row | null, key: string): boolean => phpNonEmpty(ctx.read.value(row, key));
  const ignoresLine = enabled(ability, 'ignores_line_of_effect') || enabled(attackContext, 'ignores_line_of_effect');
  const areaOrigin = enabled(ability, 'aoe_type') || enabled(ability, 'persistent_aoe')
    || enabled(attackContext, 'aoe_type') || enabled(attackContext, 'persistent_aoe');
  if (!ignoresLine && !areaOrigin) {
    if (rangedAttack(rollType)) tier = spellAttackDirectionalCover(ctx, caster, target);
    else checkLineOfEffect(ctx, caster, target);
  }
  if (tier === 'blocked') handBack('attack_roll:line-of-effect-blocked');
  const rawAc = ctx.read.value(target, 'ac_total');
  const projectedAc = record(record(record(record(target.computed)?.creature)?.ac)?.ac)?.total;
  const storedAc = rawAc == null ? requiredInt(projectedAc, 'attack_roll:ac-not-held')
    : requiredInt(rawAc, 'attack_roll:invalid-ac');
  const flags = (row: Row | null): CoverIgnoreFlags => ({
    rangedWeaponAttacksIgnoreCover: enabled(row, 'ranged_weapon_attacks_ignore_cover'),
    rangedSpellAttacksIgnoreCover: enabled(row, 'ranged_spell_attacks_ignore_cover'),
    rangedAttacksIgnoreCover: enabled(row, 'ranged_attacks_ignore_cover'),
    rangedAttacksIgnorePartialCover: enabled(row, 'ranged_attacks_ignore_partial_cover'),
  });
  const defense = coverDefenseSnapshot({ tier, effectiveDefenseDelta: tier === 'partial' ? PARTIAL_DEFENSE_DELTA
    : tier === 'heavy' ? HEAVY_DEFENSE_DELTA : 0, baseEc: dc,
    baseAc: Math.max(dc, storedAc - legacyCoverEnvironmentalAcDelta(ctx, target)), rollTypeId: rollType,
    sources: [flags(caster), flags(ability), flags(attackContext)],
  });
  let adv = meleeAttack(rollType) && !ignoresLine && !areaOrigin && isFlanked(ctx, caster, target) ? 1 : 0;
  const casterPos = combatCenter(caster), targetPos = combatCenter(target);
  const casterSize = combatSizeFt(caster, ctx.read), targetSize = combatSizeFt(target, ctx.read);
  const distance = (): number => {
    if (!casterPos || !targetPos || casterSize == null) handBack('attack_roll:range-geometry-not-held');
    return combatCenterDistanceFt(casterPos, targetPos, casterSize, targetSize);
  };
  let underwaterMiss = false;
  if (rangedAttack(rollType)) {
    const normalRaw = ctx.read.value(ability, 'range_begin');
    const longRaw = ctx.read.value(ability, 'range_end');
    const normal = normalRaw == null || normalRaw === '' ? null : Number(normalRaw);
    const long = longRaw == null || longRaw === '' ? null : Number(longRaw);
    const environment = upper(record(ctx.encounter.encounters)?.encounter_environment ?? ctx.read.value(ctx.encounter, 'location_environment'));
    if (environment === UNDERWATER && normal !== null && Number.isFinite(normal))
      underwaterMiss = distance() > normal;
    if (normal !== null && long !== null && Number.isFinite(normal) && Number.isFinite(long)) {
      // ft_creature_holds_passive_ability() can inspect grants the page may not project.
      const deadEye = ctx.read.entries(caster, 'passive_ability').some(entry => upper(itemField(record(entry), 'item_id') ?? entry) === DEAD_EYE);
      if (deadEye) handBack('check_ranged_end_to_target_for_dis_stack:dead-eye-activity-not-held');
      if (!enabled(caster, 'ignore_long_range_attack_roll_dis')
          && !enabled(ability, 'ignore_long_range_attack_roll_dis')) {
        const range = distance();
        if (range > normal && range <= long) adv -= 1;
      }
    }
  }
  adv += attackElevationAdv(ctx, caster, target, meleeAttack(rollType));
  return { effectiveAc: defense.effectiveAc, adv, underwaterMiss,
    ...(rollType === SPELL_RANGED && !ignoresLine && !areaOrigin ? { coverTier: tier } : {}) };
}

/** attack_roll.php followed by damage_roll.php's attack branch. */
export function attackRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[],
  weaponPhase?: { ability: Row; skill: string; hit?: boolean; target?: Row }): void {
  const rollType = String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase();
  const objectThrow = isThrowObjectCast(ability, input);
  if (rollType !== SPELL_MELEE && rollType !== SPELL_RANGED && !(weaponPhase && [WEAPON_MELEE, WEAPON_RANGED].includes(rollType))
      && !(objectThrow && rollType === WEAPON_RANGED)) handBack('attack_roll:weapon-roll-type');
  const nextSpell = nextSpellPlan(ability);
  const paired = !weaponPhase && targets.length === 2 && (nextSpell?.twin || nextSpell?.bounce);
  if (targets.length !== 1 && !paired) handBack('damage_roll:multi-target-validation');
  let bounce: { result: number; natural: number } | null = null;
  // damage_roll.php: inventory targets auto-connect, without an attack roll or critical face.
  if (isInventoryRow(targets[0]!)) {
    const target = targets[0]!;
    if (objectThrow) ctx.throwObjectFirstHit ??= target;
    if (weaponPhase) {
      const land = planObjectWeaponProjectile(ctx, ability, caster, target);
      weaponPhase.hit = true;
      weaponPhase.target = target;
      const original = ctx.read;
      if (land) ctx.read = { ...original, entries: land.entries };
      try {
        damageTypeRoll(ctx, input, land?.weapon ?? ability, caster, target, false, true, 0, false,
          () => postEffectConditions(ctx, input, weaponPhase.ability, caster, target) || true, weaponPhase.ability);
        land?.finish();
      } finally { ctx.read = original; }
      return;
    }
    if (!postEffectConditions(ctx, input, ability, caster, target) && ctx.read.has(ability, 'damage_type'))
      handBack('attack_roll:effect-gate-unmet');
    if (ctx.read.has(ability, 'damage_type')) damageTypeRoll(ctx, input, ability, caster, target);
    return;
  }
  // attack_roll.php applies hindered_attack_counter only when it is > 0 (swings past 1 + extra_attack); under budget it is NEGATIVE
  // (weapon_attack − extra_attack, persisted non-zero) and changes nothing — a PC with Extra Attack casting a spell attack.
  if (!weaponPhase && (Number(ctx.read.value(caster, 'hindered_attack_counter') ?? 0) > 0 || (ctx.read.has(caster, 'hindered_attack_counter')
      && !Number.isFinite(Number(ctx.read.value(caster, 'hindered_attack_counter')))))) handBack('attack_roll:caster:hindered_attack_counter');
  for (const key of ['skill_rolls_adv', 'skill_rolls_dis', 'spell_ranged_attack_rolls_adv',
    'spell_ranged_attack_rolls_dis', 'spell_melee_attack_rolls_adv', 'spell_melee_attack_rolls_dis'])
    if (ctx.read.has(caster, key)) handBack(`attack_roll:caster:${key}`);
  // The cast item's OWN roll_type_against_adv / _dis is its on-hit rider (Guiding Bolt: the ability lands on the target as a status that
  // gives the next attack against it ADV) — skill_roll_arr never reads it off $item_id_used_arr, so it is not a source on this roll.
  for (const key of ['roll_type_against_dis', 'roll_type_against_adv', 'triggers_on_roll_type_against_the_caster',
    'attack_automatically_hits', 'attack_automatically_misses', 'hit_to_crit', 'crit_to_hit'])
    if ((!key.startsWith('roll_type_against_') && (ctx.read.has(ability, key)
        || !!weaponPhase && ctx.read.has(weaponPhase.ability, key))) || ctx.read.has(targets[0]!, key)) handBack(`attack_roll:${key}`);
  const criticalThreshold = attackCriticalHitThreshold(ctx, caster, weaponPhase?.ability ?? ability, input.useAbilityVariable1);
  // summons_gain_caster_cantrips: an injected cantrip rolls the CONTROLLER's Bending + spellcasting essence (the summon still rolls the d20)
  const skill = objectThrow ? throwObjectSkill(ctx, ability, caster) : weaponPhase ? selectSkillForContestingRoll(ctx, caster, [weaponPhase.skill])
    : selectCastingSkill(ctx, caster, input.abilityId, castSkillEntries(ctx, []));
  if (weaponPhase) skill.adv -= hinderedAttackStacks(ctx, caster);
  // attack_roll.php: apply_ranged_attack_dis_within_reach() — ONE DIS stack when any living opposing-team creature stands within
  // the caster's (center, rules) reach. It runs before damage_roll's skill snapshot, so a stray re-roll keeps it.
  if (rangedAttack(rollType) && ctx.read.value(caster, weaponPhase || objectThrow
    ? 'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach' : 'ignore_ranged_spell_attack_roll_dis_within_an_enemy_reach') == null) {
    const roster = Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : [];
    for (const entry of roster) {
      const listed = record(entry);
      if (!listed) continue;
      const row = ctx.members.get(ecId(listed)) ?? listed;
      if (checkOpposingTeam(ctx, caster, row) && !phpNonEmpty(ecLean(row, 'encounter_creature_dead')) && rulesWithinReach(ctx, caster, row)) {
        skill.adv -= 1;
        break;
      }
    }
  }
  for (const target of targets) {
    if (!effectTargetProceeds(ctx, input, ability, caster, target, target === targets[0])) continue;
    const evaded = applyPreRollEvade(ctx, target, rollType, caster);
    runIncomingAllyReactions(ctx, caster, target, rollType);
    postRollReactionsPreDraw(ctx, caster, target, rollType);
    const dc = Math.max(0, requiredInt(ctx.read.value(target, 'ec_total'), 'attack_roll:ec-not-held'));
    critGates(ctx, target);
    const spatial = attackSpatial(ctx, ability, caster, target, rollType);
    // damage_roll.php's ranged natural-1 STRAY SHOT (ranged_stray_shot_helpers.php): the candidate set is state, so every
    // candidate's own gates are settled here, before the d20 (the dry run also resolves the cast against each candidate).
    if (phpNonEmpty(ctx.read.value(ability, 'attack_underwater_auto_miss')))
      handBack('attack_roll:authored-underwater-auto-miss');
    const underwater = spatial.underwaterMiss;
    const projectileId = upper(record(record(ability.computed)?.throw_object)?.inventory_id);
    const strayPool = rangedAttack(rollType) && !underwater ? strayCandidates(ctx, caster, target)
      .filter(candidate => !projectileId || inventoryId(candidate) !== projectileId) : [];
    const straySpatial = new Map<Row, AttackSpatial>();
    for (const candidate of strayPool) {
      if (isInventoryRow(candidate)) { straySpatial.set(candidate, { effectiveAc: 0, adv: 0, underwaterMiss: false }); continue; }
      critGates(ctx, candidate);
      straySpatial.set(candidate, attackSpatial(ctx, ability, caster, candidate, rollType));
      requiredInt(ctx.read.value(candidate, 'ec_total'), 'attack_roll:stray-ec-not-held');
      for (const key of ['roll_type_against_dis', 'roll_type_against_adv', 'triggers_on_roll_type_against_the_caster',
        'attack_automatically_hits', 'attack_automatically_misses', 'hit_to_crit', 'crit_to_hit'])
        if (ctx.read.has(candidate, key)) handBack(`attack_roll:stray-${key}`);
      // Pre-roll Evade and ally reactions belong to the intended target only; both post-roll passes belong to the final recipient.
      postRollReactionsPreDraw(ctx, caster, candidate, rollType);
    }
    let natural = 0;
    // The ROLLER's hit_to_crit (a held "Hit to Crit" passive / status / root grant): any hit of this attack is a critical. PHP read it only
    // off the used item (damage_roll.php ~L633), so a held carrier was silently inert there; the browser applies what the layer says. Judged
    // per rolled target (a carrier gated on the target's statuses), so a stray re-roll gets its own flag. Spell / thrown-object lane only — a
    // weapon phase keeps skill_roll_arr's named hand-back (HIT2CRIT-WEAPON).
    const hitToCrit = weaponPhase ? undefined : { held: false };
    const strayHitToCrit = weaponPhase ? undefined : { held: false };
    // Evade's against-roll DIS is active for this d20 only. The nested trigger
    // does not leave a persistent status row. A fresh copy per roll: skillRollArr folds its sources into the object.
    const reused: { result: number; natural: number } | null = nextSpell?.bounce && target !== targets[0] ? bounce : null;
    const result: number = reused ? reused.result : skillRollArr(ctx, input, caster, { ...skill, adv: skill.adv + spatial.adv - (evaded ? 1 : 0) }, target, dc,
      { rollType, criticalThreshold, coverTier: spatial.coverTier, onNatural: (face) => { natural = face; }, hitToCrit,
        ...(weaponPhase ? { weaponAttackMod: Number(ctx.read.value(ability, 'weapon_attack_mod') ?? 0) } : {}) });
    if (reused) {
      natural = reused.natural;
      insertRoll(ctx, { roll_type_1: 'bouncing_spell_reused_attack', roll_item_id: input.abilityId, by: caster, to: target,
        roll_result: result, dc });
    } else if (nextSpell?.bounce) bounce = { result, natural };
    let hitTarget = target, hitResult = result, hitNatural = natural, hitDc = dc, hitSpatial = spatial;
    const strayFirstNaturalOne = !reused && natural === 1 && rangedAttack(rollType) && !underwater;
    let doubleNaturalOne = false;
    if (strayFirstNaturalOne && strayPool.length) {
      // One weighted pick, then the attack is rolled again against the new token (the skill as it stood before the first
      // roll — damage_roll.php restores the snapshot); a second natural 1 is the legendary whiff.
      hitTarget = pickStrayCandidate(ctx, strayPool);
      hitSpatial = straySpatial.get(hitTarget)!;
      hitDc = isInventoryRow(hitTarget) ? 0 : Math.max(0, requiredInt(ctx.read.value(hitTarget, 'ec_total'), 'attack_roll:stray-ec-not-held'));
      let second = 0;
      hitResult = isInventoryRow(hitTarget) ? 1 : skillRollArr(ctx, input, caster, { ...skill, adv: skill.adv + hitSpatial.adv }, hitTarget, hitDc,
        { rollType, criticalThreshold, coverTier: hitSpatial.coverTier, onNatural: (face) => { second = face; }, hitToCrit: strayHitToCrit,
          ...(weaponPhase ? { weaponAttackMod: Number(ctx.read.value(ability, 'weapon_attack_mod') ?? 0) } : {}) });
      hitNatural = second;
      if (second === 1) {
        doubleNaturalOne = true;
        insertRoll(ctx, { roll_type_1: 'ranged_stray_double_natural_one', roll_item_id: input.abilityId, by: caster, to: hitTarget });
      }
    }
    if (weaponPhase) weaponPhase.target = hitTarget;
    // A natural 1 always misses; a connected hit uses the restored ability's critical range.
    if (hitNatural === 1 || hitResult < hitDc || hitSpatial.underwaterMiss) {
      insertRoll(ctx, { roll_type_1: weaponPhase || objectThrow ? 'weapon_attack_missed' : 'spell_attack_missed', roll_item_id: input.abilityId, by: caster, to: hitTarget });
      // damage_roll.php: a ranged natural-1 miss chain warns of the fire path (not after the double natural-1 row).
      if (strayFirstNaturalOne && !doubleNaturalOne)
        insertRoll(ctx, { roll_type_1: 'ranged_attack_missed_fire_path', roll_item_id: input.abilityId, by: caster, to: hitTarget });
      runPostRollRetaliation(ctx, caster, hitTarget, rollType, false);
      continue;
    }
    runPostRollReduce(ctx, caster, hitTarget, rollType);
    // damage_roll.php calls post_effect_conditions before calc_total_damage_and_apply_to_target
    // inserts the accumulated damage row. Preserve that row order.
    // R-108 push collision (lane A5): a damaging push / pull runs its effects inside damageTypeRoll's `between` hook — PHP prepares the
    // fragment (damage_type_rolls) before post_effect_conditions, so the collision's d6s follow the damage dice.
    // …and a CRITICAL under the Remarkable cascade (lane A7): check_critical_hit_logic logs its d20 row while damage_type_rolls PREPARES the
    // fragment — before post_effect_conditions' rows (an on-hit status) — so the crit fragment is prepared first there too.
    if (weaponPhase) weaponPhase.hit = true;
    if (objectThrow) ctx.throwObjectFirstHit ??= hitTarget;
    const critical = hitNatural >= criticalThreshold || deathTargetAutoCritical(ctx, hitTarget, rollType)
      || !!(hitTarget === target ? hitToCrit : strayHitToCrit)?.held;
    const prepared = !!weaponPhase || damagingForcedMove(ctx, ability)
      || (critical && ctx.read.has(ability, 'damage_type') && ctx.read.houseRules?.()?.remarkable === true);
    // calc_total runs only when post_effect_conditions was MET (an immune target: nullify_post_effect_conditions), except the ATTACK
    // ABILITY fallback (post_effect_conditions.php ~L1140: a weapon hit applies its damage whatever the ability's effects gate said).
    const fallback = ['attack_ability', 'opportunity_attack_ability'].includes(String(itemField(ability, 'item_type') ?? ''));
    // PHP judged the fragment's status gates at that draw: a rider keyed on a status these effects remove (Animus, owner ruling 65) reads the
    // rows held before them (withFragmentGateStatuses — the gates only; the application reads live state)
    const atDraw = prepared ? (damage: () => void) => damage() : withFragmentGateStatuses(ctx, hitTarget);
    if (!prepared) {
      const met = postEffectConditions(ctx, input, ability, caster, hitTarget);
      // PHP drew this fragment before the gate and skips only its application: not modelled on this path → PHP (state, pre-draw)
      if (!met && !fallback && ctx.read.has(ability, 'damage_type')) handBack('attack_roll:effect-gate-unmet');
    }
    if (ctx.read.has(ability, 'damage_type')) {
      // calc_total_damage_and_apply_to_target.php applies dr_total only for an EC hit.
      // The computed creature AC projection can supply this even when no layer sits on the root.
      const rawDr = ctx.read.value(hitTarget, 'dr_total');
      const projectedDr = record(record(record(hitTarget.computed)?.creature)?.ac)?.dr;
      const dr = rawDr == null ? (projectedDr == null ? 0 : requiredInt(projectedDr, 'attack_roll:invalid-dr'))
        : requiredInt(rawDr, 'attack_roll:invalid-dr');
      // damage_type_rolls.php: a CRITICAL is always an 'AC' hit — no damage reduction, whatever the total.
      atDraw(() => damageTypeRoll(ctx, input, ability, caster, hitTarget, false, !!weaponPhase,
        !critical && hitResult < hitSpatial.effectiveAc ? dr : 0, critical,
        // the prepared fragment applies when the effects' gate is met (or the attack-ability fallback)
        prepared ? () => postEffectConditions(ctx, input, weaponPhase?.ability ?? ability, caster, hitTarget) || !!weaponPhase || fallback : undefined,
        weaponPhase?.ability));
    }
    runPostRollRetaliation(ctx, caster, hitTarget, rollType, true);
  }
}
